import { useState, useCallback, useEffect, useRef } from 'react';
import { Layout, Menu, Button, Dropdown, message, Tooltip, Tag, Modal, Form, Input } from 'antd';
import {
  DashboardOutlined,
  AppstoreOutlined,
  EyeOutlined,
  MessageOutlined,
  CalendarOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  BulbOutlined,
  BulbFilled,
  ClockCircleOutlined,
  EnvironmentOutlined,
  DatabaseOutlined,
  CommentOutlined,
  UnorderedListOutlined,
  SolutionOutlined,
  WarningOutlined,
  ApiOutlined,
  WifiOutlined,
  ReloadOutlined,
  LineChartOutlined,
  RadarChartOutlined,
  ScheduleOutlined,
  BookOutlined,
  ReadOutlined,
  TeamOutlined,
  ClusterOutlined,
  CheckCircleOutlined,
  EditOutlined,
  FormOutlined,
  FileTextOutlined,
  ColumnHeightOutlined,
} from '@ant-design/icons';
import { WiDayCloudy, WiRain } from 'weather-icons-react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.tsx';
import { useSeason } from '../../hooks/useSeason';
import { useEvent } from '../../hooks/useEvent';
import { useActiveEvent } from '../../hooks/useActiveEvent';
import { useTheme } from '../../contexts/useTheme';
import { useChartAnimations } from '../../contexts/useChartAnimations';
import { useTopBarStation } from '../../contexts/useTopBarStation';
import { SeasonSelector } from '../Common/SeasonSelector';
import { EventSelector } from '../Common/EventSelector';
import { OfficialAirTemp } from '../OfficialAirTemp';
import { TrackTemperature } from '../TrackTemperature';
import { LightIntensity } from '../LightIntensity';
import { WBGT } from '../WBGT';
import { HeatIndex } from '../HeatIndex';
import { ObservationTimestamp } from '../ObservationTimestamp';
import { RainForecastHeader } from '../RainForecastHeader';
import { ROUTES } from '../../utils/constants';
import { canAccessDashboards } from '../../utils/dashboardAccess';
import { parseDateOnly } from '../../utils/dateUtils';
import eventsServiceInstance, { type EventWithCircuit } from '../../api/eventsService';
import seasonsServiceInstance, { type Season } from '../../api/seasonsService';
import { FeedbackModal } from '../Feedback';
import { AlertBadge } from '../AlertNotifications';
import { useStationObservations } from '../../contexts/useStationObservations';
import systemConfigService, { parseConfigValue } from '../../api/systemConfigService';
import usersService from '../../api/usersService';
import type { MenuProps } from 'antd';

interface StationIpMapping {
  reference: string;
  ip_address: string;
}

const { Header, Sider, Content } = Layout;

// Radar GCS bucket names (must match backend radarStatus.ts); open in Cloud Console
const GCS_CONSOLE_BROWSER = 'https://console.cloud.google.com/storage/browser';
const RADAR_BUCKETS = [
  { key: 'radar-bucket-input', label: 'Input (L2A) bucket', bucket: 'fia-radar-input-l2a-data' },
  { key: 'radar-bucket-output', label: 'Output (NetCDF) bucket', bucket: 'fia-portal-nc' },
  { key: 'radar-bucket-tiles', label: 'PNG Tiles bucket', bucket: 'fia-portal-tiles-2' },
] as const;

// Weather icons size to match Ant Design menu icons
const WEATHER_ICON_SIZE = 20;

const menuIcons: Record<string, React.ReactNode> = {
  dashboard: <DashboardOutlined />,
  forecast: <WiDayCloudy size={WEATHER_ICON_SIZE} />,
  nowcasting: <RadarChartOutlined />,
  observations: <EyeOutlined />,
  messages: <MessageOutlined />,
  alerts: <WarningOutlined />,
  rainForecasts: <WiRain size={WEATHER_ICON_SIZE} />,
  settings: <SettingOutlined />,
  forecastFields: <DatabaseOutlined />,
  observationFields: <DatabaseOutlined />,
  circuits: <EnvironmentOutlined />,
  teams: <TeamOutlined />,
  events: <CalendarOutlined />,
  jobs: <ScheduleOutlined />,
  forecastManagement: <FormOutlined />,
  feedbackManagement: <SolutionOutlined />,
  systemConfig: <ApiOutlined />,
  apiDocs: <BookOutlined />,
  portalDocs: <ReadOutlined />,
  adminDocs: <BookOutlined />,
  stations: <ClusterOutlined />,
  radarStatus: <RadarChartOutlined />,
  status: <CheckCircleOutlined />,
  userManagement: <UserOutlined />,
  dashboards: <AppstoreOutlined />,
};

export const AppLayout = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [loadingCurrentEvent, setLoadingCurrentEvent] = useState(false);
  const [headerCompact, setHeaderCompact] = useState(() => {
    try { return localStorage.getItem('header-weather-compact') === 'true'; } catch { return false; }
  });
  const handleHeaderCompactChange = useCallback((v: boolean) => {
    setHeaderCompact(v);
    try { localStorage.setItem('header-weather-compact', String(v)); } catch { /* noop */ }
  }, []);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [stationIpAddresses, setStationIpAddresses] = useState<StationIpMapping[]>([]);
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profilePhoneValid, setProfilePhoneValid] = useState(true);
  const [profileForm] = Form.useForm<{ firstName: string; lastName: string; phoneNumber: string }>();
  const profileFormValues = Form.useWatch([], profileForm);
  const { authState, logout, setAuthState } = useAuth();
  const { selectedSeason, setSelectedSeason } = useSeason();
  const { selectedEvent, setSelectedEvent, storedEventKey } = useEvent();
  const { activeEvent, loading: activeEventLoading } = useActiveEvent();
  const [eventBootstrapComplete, setEventBootstrapComplete] = useState(false);
  const hasBootstrappedEvent = useRef(false);
  const { theme, toggleTheme } = useTheme();
  const { chartAnimationsEnabled, toggleChartAnimations } = useChartAnimations();
  const { topBarStationId, setTopBarStationId } = useTopBarStation();
  const { dataSource, setDataSource, isTioAvailable, tioLoading, refresh, refreshing } = useStationObservations();
  const user = authState.user;
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  const handleOpenEditProfile = () => {
    if (user) {
      profileForm.setFieldsValue({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: (user as { phoneNumber?: string }).phoneNumber || '',
      });
      setProfilePhoneValid(true);
      setEditProfileModalOpen(true);
    }
  };

  const handleEditProfileSubmit = async () => {
    if (!user || !profilePhoneValid) return;
    try {
      const values = await profileForm.validateFields();
      setProfileSaving(true);

      const payload: { firstName: string; lastName: string; phoneNumber?: string } = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      };
      if (values.phoneNumber?.trim()) {
        payload.phoneNumber = values.phoneNumber.trim();
      }

      const res = await usersService.updateProfile(payload);
      if (res.success && res.data && authState.user) {
        const updatedUser = {
          ...authState.user,
          firstName: res.data.firstName,
          lastName: res.data.lastName,
          name: res.data.name,
          phoneNumber: res.data.phoneNumber,
        };
        setAuthState({
          ...authState,
          user: updatedUser,
        });
        message.success('Profile updated successfully');
        setEditProfileModalOpen(false);
        profileForm.resetFields();
      } else {
        message.error(res.errorData?.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSeasonChange = useCallback((_year: number, season: Season) => {
    setSelectedSeason(season);
    // Clear event selection when season changes
    setSelectedEvent(null);
  }, [setSelectedSeason, setSelectedEvent]);

  const handleEventChange = useCallback((event: EventWithCircuit) => {
    setSelectedEvent(event);
  }, [setSelectedEvent]);

  // On load, prefer the active event over the last stored event key.
  // Only restore from localStorage when no event is currently active.
  useEffect(() => {
    if (activeEventLoading || hasBootstrappedEvent.current) return;
    hasBootstrappedEvent.current = true;

    const finishBootstrap = () => setEventBootstrapComplete(true);

    if (!activeEvent) {
      finishBootstrap();
      return;
    }

    seasonsServiceInstance
      .getAllSeasons()
      .then((response) => {
        if (response.success && response.data) {
          const season = response.data.find((s) => s.year === activeEvent.season_year);
          if (season) {
            setSelectedSeason(season);
          }
        }
        setSelectedEvent(activeEvent);
      })
      .catch((error) => {
        console.error('Error bootstrapping active event:', error);
        setSelectedEvent(activeEvent);
      })
      .finally(finishBootstrap);
  }, [activeEvent, activeEventLoading, setSelectedSeason, setSelectedEvent]);

  const handleGoToCurrentEvent = useCallback(async () => {
    setLoadingCurrentEvent(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Get all seasons to search through
      const seasonsResponse = await seasonsServiceInstance.getAllSeasons();
      if (!seasonsResponse.success || !seasonsResponse.data) {
        message.error('Failed to load seasons');
        return;
      }

      // Search through seasons starting with the current year
      const currentYear = today.getFullYear();
      const sortedSeasons = [...seasonsResponse.data].sort((a, b) => b.year - a.year);

      let currentEvent: EventWithCircuit | null = null;
      let currentEventSeason: Season | null = null;

      for (const season of sortedSeasons) {
        // Only check seasons within a reasonable range (current year +/- 1)
        if (Math.abs(season.year - currentYear) > 1) continue;

        const eventsResponse = await eventsServiceInstance.getAllEvents(season.year);
        if (eventsResponse.success && eventsResponse.data) {
          const foundEvent = eventsResponse.data.find(e => {
            const start = parseDateOnly(e.start_date);
            const end = parseDateOnly(e.end_date);
            end.setHours(23, 59, 59, 999);
            return today >= start && today <= end;
          });

          if (foundEvent) {
            currentEvent = foundEvent;
            currentEventSeason = season;
            break;
          }
        }
      }

      if (currentEvent && currentEventSeason) {
        setSelectedSeason(currentEventSeason);
        setSelectedEvent(currentEvent);
        message.success(`Navigated to ${currentEvent.circuit.display_name}`);
      } else {
        message.info('No event is currently taking place');
      }
    } catch (error) {
      console.error('Error finding current event:', error);
      message.error('Failed to find current event');
    } finally {
      setLoadingCurrentEvent(false);
    }
  }, [setSelectedSeason, setSelectedEvent]);

  // Fetch station IP addresses for root users (Stations menu)
  useEffect(() => {
    if (user?.role !== 'root' || !authState.token) {
      setStationIpAddresses([]);
      return;
    }
    systemConfigService
      .getConfigByKey('stations.ip_addresses', authState.token)
      .then((config) => {
        if (!config.config_value) {
          setStationIpAddresses([]);
          return;
        }
        const parsed = parseConfigValue(config.config_value, config.value_type);
        if (Array.isArray(parsed)) {
          setStationIpAddresses(
            parsed.filter(
              (item: unknown): item is StationIpMapping =>
                typeof item === 'object' &&
                item !== null &&
                'reference' in item &&
                'ip_address' in item &&
                typeof (item as StationIpMapping).reference === 'string' &&
                typeof (item as StationIpMapping).ip_address === 'string'
            )
          );
        } else {
          setStationIpAddresses([]);
        }
      })
      .catch(() => {
        setStationIpAddresses([]);
      });
  }, [user?.role, authState.token]);

  // Determine if selected event is legacy or non-legacy
  const isLegacyEvent = selectedEvent?.legacy === true;
  const isNonLegacyEvent = selectedEvent && !selectedEvent.legacy;
  // Check if observations are enabled (default to true for backward compatibility)
  const observationsEnabled = selectedEvent?.observations_enabled !== false;

  // Main navigation items
  const mainMenuItems: MenuProps['items'] = [
    {
      key: 'climate-recap',
      icon: menuIcons.dashboard,
      label: 'Climate Recap',
      onClick: () => navigate(ROUTES.CLIMATE_RECAP),
    },
    {
      key: 'forecast',
      icon: menuIcons.forecast,
      label: 'Forecast',
      onClick: () => navigate(ROUTES.FORECAST),
    },
    {
      key: 'nowcasting',
      icon: menuIcons.nowcasting,
      label: 'Nowcasting',
      onClick: () => navigate(ROUTES.NOWCASTING),
    },
    // Observations - only show if observations are enabled
    ...(observationsEnabled ? [{
      key: 'observations',
      icon: menuIcons.observations,
      label: 'Observations',
      onClick: () => navigate(ROUTES.OBSERVATIONS),
    }] : []),
    // Messages - only show for legacy events (or when no event selected)
    ...(!selectedEvent || isLegacyEvent ? [{
      key: 'messages',
      icon: menuIcons.messages,
      label: 'Messages',
      onClick: () => navigate(ROUTES.MESSAGES),
    }] : []),
    // Alerts - only show for non-legacy events (including demo)
    ...(isNonLegacyEvent ? [{
      key: 'alerts',
      icon: menuIcons.alerts,
      label: 'Alerts',
      onClick: () => navigate(ROUTES.ALERTS),
    }] : []),
    // Rain Forecasts - always visible
    {
      key: 'rain-forecasts',
      icon: menuIcons.rainForecasts,
      label: 'Rain Forecasts',
      onClick: () => navigate(ROUTES.RAIN_FORECASTS),
    },
    // Dashboards - hidden for blocked account IDs (root always allowed)
    ...(canAccessDashboards(user) ? [{
      key: 'dashboards',
      icon: menuIcons.dashboards,
      label: 'My Dashboards',
      onClick: () => navigate(ROUTES.DASHBOARDS),
    }] : []),
    // Event Jobs - only for root users when a non-legacy event is selected
    ...(user?.role === 'root' && isNonLegacyEvent ? [
      {
        key: 'jobs',
        icon: menuIcons.jobs,
        label: 'Jobs',
        onClick: () => navigate(ROUTES.JOBS),
      },
      {
        key: 'forecast-management',
        icon: menuIcons.forecastManagement,
        label: 'Forecast Management',
        onClick: () => navigate(ROUTES.FORECAST_MANAGEMENT),
      },
    ] : []),
  ];

  // Bottom menu items (stations for root only, settings for root only, then events)
  const stationMenuChildren: MenuProps['items'] = stationIpAddresses.map((s: StationIpMapping) => ({
    key: `station-${s.reference}`,
    label: `Station ${s.reference}`,
    onClick: () => window.open(`https://${s.ip_address}`, '_blank', 'noopener,noreferrer'),
  }));

  const bottomMenuItems: MenuProps['items'] = [
    // Settings submenu (admin) - only visible for root users; includes Stations
    ...(user?.role === 'root' ? [{
      key: 'settings',
      icon: menuIcons.settings,
      label: 'Settings',
      children: [
        {
          key: 'radar',
          icon: menuIcons.radarStatus,
          label: 'Radar',
          children: [
            {
              key: 'radar-status',
              icon: menuIcons.status,
              label: 'Status',
              onClick: () => navigate(ROUTES.RADAR_STATUS),
            },
            {
              key: 'radar-l2-processing',
              icon: <FileTextOutlined />,
              label: 'L2 Processing Log',
              onClick: () => navigate(ROUTES.RADAR_L2_INGEST),
            },
            { type: 'divider' as const, key: 'radar-divider' },
            ...RADAR_BUCKETS.map(({ key, label, bucket }) => ({
              key,
              label,
              onClick: () => window.open(`${GCS_CONSOLE_BROWSER}/${bucket}`, '_blank', 'noopener,noreferrer'),
            })),
          ],
        },
        {
          key: 'stations',
          icon: menuIcons.stations,
          label: 'Stations',
          children: [
            {
              key: 'stations-status',
              icon: menuIcons.status,
              label: 'Status',
              onClick: () => navigate(ROUTES.STATIONS_STATUS),
            },
            {
              key: 'stations-ingest',
              icon: <FileTextOutlined />,
              label: 'Ingest Log',
              onClick: () => navigate(ROUTES.STATION_INGEST),
            },
            { type: 'divider' as const, key: 'stations-divider' },
            ...(stationMenuChildren && stationMenuChildren.length > 0 ? stationMenuChildren : [{ key: 'stations-empty', label: 'No stations configured', disabled: true }]),
          ],
        },
        {
          key: 'forecast-fields',
          icon: menuIcons.forecastFields,
          label: 'Forecast Fields',
          onClick: () => navigate(ROUTES.FORECAST_FIELDS),
        },
        {
          key: 'observation-fields',
          icon: menuIcons.observationFields,
          label: 'Observation Fields',
          onClick: () => navigate(ROUTES.OBSERVATION_FIELDS),
        },
        {
          key: 'circuits-admin',
          icon: menuIcons.circuits,
          label: 'Circuits',
          onClick: () => navigate(ROUTES.CIRCUITS),
        },
        {
          key: 'teams',
          icon: menuIcons.teams,
          label: 'Teams',
          onClick: () => navigate(ROUTES.TEAMS),
        },
        {
          key: 'system-config',
          icon: menuIcons.systemConfig,
          label: 'System Config',
          onClick: () => navigate(ROUTES.SYSTEM_CONFIG),
        },
        {
          key: 'user-management',
          icon: menuIcons.userManagement,
          label: 'User Management',
          onClick: () => navigate(ROUTES.USER_MANAGEMENT),
        },
        {
          key: 'admin-docs',
          icon: menuIcons.adminDocs,
          label: 'Admin Documentation',
          onClick: () => navigate(ROUTES.ADMIN_DOCS),
        },
      ],
    }] : []),
    // User Management - for root and account-admin (when not root, account-admin needs access)
    ...(user?.role === 'account-admin' ? [{
      key: 'user-management',
      icon: menuIcons.userManagement,
      label: 'User Management',
      onClick: () => navigate(ROUTES.USER_MANAGEMENT),
    }] : []),
    // Circuits (climate tools) - visible for all users
    {
      key: 'circuits-climate',
      icon: menuIcons.circuits,
      label: 'Circuits',
      onClick: () => navigate(ROUTES.CIRCUITS_CLIMATE),
    },
    // Events - visible for all users
    {
      key: 'events',
      icon: menuIcons.events,
      label: 'Events',
      onClick: () => navigate(ROUTES.EVENTS),
    },
    // API Documentation - visible for all users
    {
      key: 'api-docs',
      icon: menuIcons.apiDocs,
      label: 'API Documentation',
      onClick: () => navigate(ROUTES.API_DOCS),
    },
    // Portal User Guide - visible for all users
    {
      key: 'portal-docs',
      icon: menuIcons.portalDocs,
      label: 'Portal User Guide',
      onClick: () => navigate(ROUTES.PORTAL_DOCS),
    },
    // Timelines API - only for root and account-admin
    ...(user?.role === 'root' || user?.role === 'account-admin' ? [{
      key: 'timelines-api',
      icon: <LineChartOutlined />,
      label: 'Timelines API',
      onClick: () => navigate(ROUTES.TIMELINES_API),
    }] : []),
  ];

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontWeight: 600 }}>
            {user?.name || `${user?.firstName} ${user?.lastName}`}
          </span>
          {user?.teamDisplayName && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <TeamOutlined style={{ fontSize: 12 }} />
              <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>{user.teamDisplayName}</Tag>
            </div>
          )}
        </div>
      ),
      disabled: true,
      style: {
        cursor: 'default',
        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
      },
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'edit-profile',
      icon: <EditOutlined />,
      label: 'Edit Profile',
      onClick: handleOpenEditProfile,
    },
    {
      type: 'divider' as const,
    },
    // Feedback options - available for root, account-admin, collaborator, and developer roles
    ...(user?.role === 'root' || user?.role === 'account-admin' || user?.role === 'collaborator' || user?.role === 'developer' ? [
      {
        key: 'feedback',
        icon: <CommentOutlined />,
        label: 'Send Feedback',
        onClick: () => setFeedbackModalOpen(true),
      },
      {
        key: 'my-feedback',
        icon: <UnorderedListOutlined />,
        label: 'My Feedback',
        onClick: () => navigate(ROUTES.FEEDBACK),
      },
      {
        key: 'team-feedback',
        icon: <TeamOutlined />,
        label: 'My Team Feedback',
        onClick: () => navigate(ROUTES.FEEDBACK_TEAM),
      },
    ] : []),
    // Feedback Management - only for root
    ...(user?.role === 'root' ? [
      {
        key: 'feedback-management',
        icon: <SolutionOutlined />,
        label: 'Feedback Management',
        onClick: () => navigate(ROUTES.FEEDBACK_MANAGEMENT),
      },
    ] : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.includes('radar/l2-processing')) return 'radar-l2-processing';
    if (path.includes('radar/status')) return 'radar-status';
    if (path.includes('stations/ingest')) return 'stations-ingest';
    if (path.includes('stations/status')) return 'stations-status';
    if (path.includes('timelines-api')) return 'timelines-api';
    if (path.includes('admin-docs')) return 'admin-docs';
    if (path.includes('portal-docs')) return 'portal-docs';
    if (path.includes('api-docs')) return 'api-docs';
    if (path.includes('dashboards')) return 'dashboards';
    if (path.includes('climate-recap')) return 'climate-recap';
    if (path.includes('feedback-management')) return 'feedback-management';
    if (path.includes('feedback/team')) return 'team-feedback';
    if (path.includes('feedback')) return 'my-feedback';
    if (path.includes('observation-fields')) return 'observation-fields';
    if (path.includes('forecast-fields')) return 'forecast-fields';
    if (path.includes('system-config')) return 'system-config';
    if (path.includes('jobs')) return 'jobs';
    if (path.includes('forecast-management')) return 'forecast-management';
    if (path.includes('rain-forecasts')) return 'rain-forecasts';
    if (path.includes('forecast')) return 'forecast';
    if (path.includes('nowcasting')) return 'nowcasting';
    if (path.includes('observations')) return 'observations';
    if (path.includes('messages')) return 'messages';
    if (path.includes('alerts')) return 'alerts';
    if (path.includes('teams')) return 'teams';
    if (path.includes('circuits/climate')) return 'circuits-climate';
    if (path.includes('circuits')) return 'circuits-admin';
    if (path.includes('events')) return 'events';
    if (path.includes('user-management')) return 'user-management';
    return 'climate-recap';
  };

  const settingsMenuItems: MenuProps['items'] = [
    {
      key: 'settings-label',
      label: (
        <div style={{ fontSize: 11, color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', padding: '4px 0' }}>
          Settings
        </div>
      ),
      disabled: true,
    },
    {
      key: 'theme',
      // Light mode = outline bulb; dark mode = dark-filled bulb (not a solid “lit” bulb for Light Mode)
      icon:
        theme === 'dark' ? (
          <BulbOutlined />
        ) : (
          <BulbFilled style={{ color: '#262626' }} />
        ),
      label: theme === 'dark' ? 'Light Mode' : 'Dark Mode',
      onClick: toggleTheme,
    },
    {
      key: 'chart-animations',
      icon: <LineChartOutlined />,
      label: chartAnimationsEnabled ? 'Disable Chart Animations' : 'Enable Chart Animations',
      onClick: toggleChartAnimations,
    },
  ];

  const getOpenKeys = () => {
    const path = location.pathname;
    if (path.includes('forecast-fields') || path.includes('observation-fields') || path.includes('circuits') || path.includes('teams') || path.includes('system-config') || path.includes('user-management') || path.includes('admin-docs')) {
      return ['settings'];
    }
    if (path.includes('radar')) {
      return ['settings', 'radar'];
    }
    if (path.includes('stations/status') || path.includes('stations/ingest')) {
      return ['settings', 'stations'];
    }
    return [];
  };

  return (
    <Layout style={{
      minHeight: '100vh',
      background: theme === 'dark' ? '#141414' : '#f0f2f5',
    }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        onBreakpoint={(broken) => {
          if (broken) {
            setCollapsed(true);
          }
        }}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: theme === 'dark' ? '#141414' : '#ffffff',
          borderRight: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)',
        }}
      >
        {/* Clickable Edge to Toggle Sidebar */}
        <div
          onClick={() => setCollapsed(!collapsed)}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '8px',
            cursor: 'ew-resize',
            zIndex: 1000,
            background: 'transparent',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}>
          {/* Tomorrow.io Logo */}
          <div
            style={{
              padding: collapsed ? '16px 8px 12px 8px' : '16px 16px 12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img
              src={theme === 'dark' ? '/tio-logo-white.svg' : '/tio-logo-dark.svg'}
              alt="Tomorrow.io Logo"
              style={{
                width: collapsed ? '32px' : '120px',
                height: collapsed ? '32px' : 'auto',
                transition: 'all 0.2s',
                display: 'block',
                opacity: 0.8,
                objectFit: collapsed ? 'cover' : 'contain',
                objectPosition: 'left center',
              }}
            />
          </div>

          {/* Logo and Title */}
          <div
            style={{
              margin: '0 16px 16px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
            }}
          >
            <div
              style={{
                background: '#102D5C',
                padding: collapsed ? '6px' : '6px 10px',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'padding 0.2s',
              }}
            >
              <img
                src="/fia-logo.svg"
                alt="FIA Logo"
                style={{
                  height: collapsed ? '28px' : '32px',
                  width: 'auto',
                  transition: 'height 0.2s',
                  display: 'block',
                }}
              />
            </div>
            {!collapsed && (
              <span
                style={{
                  color: theme === 'dark' ? 'white' : 'black',
                  fontWeight: 'bold',
                  fontSize: '16px',
                }}
              >
                FIA Weather
              </span>
            )}
          </div>

          {/* Main Menu - takes up remaining space */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <Menu
              mode="inline"
              selectedKeys={[getSelectedKey()]}
              items={mainMenuItems}
              style={{
                background: 'transparent',
                border: 'none',
              }}
            />
          </div>

          {/* Bottom Menu - Circuits (root only) + Events - docked at bottom */}
          <div>
            <div
              style={{
                height: '1px',
                background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                margin: collapsed ? '8px 12px' : '8px 16px',
              }}
            />
            <Menu
              mode="inline"
              selectedKeys={[getSelectedKey()]}
              defaultOpenKeys={getOpenKeys()}
              items={bottomMenuItems}
              style={{
                background: 'transparent',
                border: 'none',
              }}
            />
          </div>
        </div>
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s' }}>
        <Header
          className="app-header"
          style={{
            padding: '0 16px',
            background: theme === 'dark' ? '#141414' : '#ffffff',
            display: 'flex',
            alignItems: 'center',
            borderBottom: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)',
            gap: '12px',
            overflow: 'hidden',
          }}
        >
          {/* Left section - selectors (flex-shrink: 0 to prevent compression) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {/* Hide season and event selectors on management/settings pages (except jobs which needs event context) */}
            {location.pathname !== ROUTES.EVENTS &&
              location.pathname !== ROUTES.FORECAST_FIELDS &&
              location.pathname !== ROUTES.CIRCUITS &&
              location.pathname !== ROUTES.TEAMS &&
              location.pathname !== ROUTES.SYSTEM_CONFIG &&
              !location.pathname.startsWith(ROUTES.API_DOCS) &&
              !location.pathname.startsWith(ROUTES.PORTAL_DOCS) &&
              !location.pathname.startsWith(ROUTES.ADMIN_DOCS) &&
              location.pathname !== ROUTES.TIMELINES_API &&
              location.pathname !== ROUTES.RADAR_STATUS &&
              location.pathname !== ROUTES.STATIONS_STATUS &&
              location.pathname !== ROUTES.USER_MANAGEMENT && (
                <>
                  <SeasonSelector
                    value={selectedSeason?.year}
                    onChange={handleSeasonChange}
                  />
                  <EventSelector
                    seasonYear={
                      eventBootstrapComplete
                        ? (selectedEvent?.season_year ?? selectedSeason?.year)
                        : undefined
                    }
                    value={
                      selectedEvent
                        ? `${selectedEvent.season_year}-${selectedEvent.circuit_id}-${selectedEvent.start_date}`
                        : eventBootstrapComplete
                          ? storedEventKey ?? undefined
                          : undefined
                    }
                    onChange={handleEventChange}
                  />
                  <Tooltip title="Go to Current Event">
                    <Button
                      type="default"
                      icon={<ClockCircleOutlined />}
                      onClick={handleGoToCurrentEvent}
                      loading={loadingCurrentEvent}
                    />
                  </Tooltip>
                </>
              )}
          </div>

          {/* Left separator */}
          <div style={{
            width: '1px',
            height: '32px',
            background: theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
            flexShrink: 0,
          }} />

          {/* Middle section - weather cards with horizontal scroll, right-aligned */}
          <div
            className="header-weather-cards"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: headerCompact ? '6px' : '10px',
              flex: 1,
              overflowX: 'auto',
              overflowY: 'hidden',
              minWidth: 0,
              paddingBottom: '4px',
              marginBottom: '-4px',
              scrollbarWidth: 'thin',
              scrollbarColor: theme === 'dark' ? 'rgba(255,255,255,0.2) transparent' : 'rgba(0,0,0,0.15) transparent',
            }}
          >
            <RainForecastHeader compact={headerCompact} />
            <ObservationTimestamp compact={headerCompact} />
            <OfficialAirTemp compact={headerCompact} />
            <TrackTemperature compact={headerCompact} />
            <LightIntensity compact={headerCompact} />
            <WBGT compact={headerCompact} />
            <HeatIndex compact={headerCompact} />
            <Tooltip title={headerCompact ? 'Expand weather bar' : 'Compact weather bar'}>
              <Button
                type="text"
                size="small"
                icon={<ColumnHeightOutlined />}
                onClick={() => handleHeaderCompactChange(!headerCompact)}
                style={{ flexShrink: 0, opacity: 0.6 }}
              />
            </Tooltip>
          </div>

          {/* Right separator */}
          <div style={{
            width: '1px',
            height: '32px',
            background: theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
            flexShrink: 0,
          }} />

          {/* Fixed right section - actions that should always be visible */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
            {/* Data Source Toggle Dropdown - Stations always shown; t.io only for active events */}
            {location.pathname !== ROUTES.EVENTS &&
              location.pathname !== ROUTES.FORECAST_FIELDS &&
              location.pathname !== ROUTES.CIRCUITS &&
              location.pathname !== ROUTES.TEAMS &&
              location.pathname !== ROUTES.SYSTEM_CONFIG &&
              !location.pathname.startsWith(ROUTES.API_DOCS) &&
              !location.pathname.startsWith(ROUTES.PORTAL_DOCS) &&
              !location.pathname.startsWith(ROUTES.ADMIN_DOCS) &&
              location.pathname !== ROUTES.TIMELINES_API &&
              location.pathname !== ROUTES.RADAR_STATUS &&
              location.pathname !== ROUTES.STATIONS_STATUS &&
              location.pathname !== ROUTES.USER_MANAGEMENT && (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'label',
                      label: (
                        <div style={{ fontSize: 11, color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', padding: '4px 0' }}>
                          Weather Data Source
                        </div>
                      ),
                      disabled: true,
                    },
                    // Always show Station Observations when observations are enabled (even with no data)
                    ...(observationsEnabled ? [{
                      key: 'stations',
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <WifiOutlined rotate={90} />
                          <span>Station Observations</span>
                          {dataSource === 'stations' && <span style={{ marginLeft: 'auto', color: '#1890ff' }}>✓</span>}
                        </div>
                      ),
                      ...(dataSource === 'stations'
                        ? {
                            children: [
                              { key: 'station-1', label: `Station 1${topBarStationId === '1' ? ' ✓' : ''}`, onClick: () => { setDataSource('stations'); setTopBarStationId('1'); } },
                              { key: 'station-2', label: `Station 2${topBarStationId === '2' ? ' ✓' : ''}`, onClick: () => { setDataSource('stations'); setTopBarStationId('2'); } },
                              { key: 'station-3', label: `Station 3${topBarStationId === '3' ? ' ✓' : ''}`, onClick: () => { setDataSource('stations'); setTopBarStationId('3'); } },
                              { key: 'station-4', label: `Station 4${topBarStationId === '4' ? ' ✓' : ''}`, onClick: () => { setDataSource('stations'); setTopBarStationId('4'); } },
                              { type: 'divider' as const },
                              { key: 'station-avg', label: `Average (All Stations)${topBarStationId === 'avg' ? ' ✓' : ''}`, onClick: () => { setDataSource('stations'); setTopBarStationId('avg'); } },
                            ],
                          }
                        : { onClick: () => setDataSource('stations') }
                      ),
                    }] : []),
                    // t.io source only available for active events
                    ...(isTioAvailable ? [{
                      key: 'tio-api',
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ApiOutlined />
                          <span>Tomorrow.io API</span>
                          {dataSource === 'tio-api' && <span style={{ marginLeft: 'auto', color: '#1890ff' }}>✓</span>}
                        </div>
                      ),
                      onClick: () => setDataSource('tio-api'),
                    }] : []),
                    { type: 'divider' },
                    {
                      key: 'refresh',
                      label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ReloadOutlined spin={refreshing} />
                          <span>Refresh Data</span>
                        </div>
                      ),
                      onClick: refresh,
                      disabled: refreshing,
                    },
                  ],
                }}
                placement="bottomRight"
                trigger={['hover', 'click']}
              >
                <Tooltip title="Data Source">
                  <Button
                    type="text"
                    icon={dataSource === 'stations' ? <WifiOutlined rotate={90} /> : <ApiOutlined />}
                    loading={tioLoading}
                    style={{
                      color: theme === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)',
                    }}
                  />
                </Tooltip>
              </Dropdown>
            )}
            <AlertBadge />
            <Dropdown menu={{ items: settingsMenuItems }} placement="bottomRight">
              <Tooltip title="Settings">
                <Button
                  type="text"
                  icon={<SettingOutlined />}
                  style={{
                    color: theme === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)',
                  }}
                />
              </Tooltip>
            </Dropdown>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Button type="text" icon={<UserOutlined />} />
            </Dropdown>
          </div>
        </Header>
        <Content
          className="app-layout-content"
          style={{
            minHeight: 280,
            height: 'calc(100vh - 64px)',
            overflow: 'auto',
            background: theme === 'dark' ? '#141414' : '#f0f2f5',
          }}
        >
          <Outlet />
        </Content>
      </Layout>

      {/* Feedback Modal */}
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
      />

      {/* Edit Profile Modal */}
      <Modal
        title="Edit Profile"
        open={editProfileModalOpen}
        onCancel={() => setEditProfileModalOpen(false)}
        onOk={handleEditProfileSubmit}
        confirmLoading={profileSaving}
        okButtonProps={{
          disabled: (() => {
            const v = profileFormValues ?? profileForm.getFieldsValue();
            return !(v?.firstName?.trim() && v?.lastName?.trim()) || !profilePhoneValid;
          })(),
        }}
        destroyOnHidden
      >
        <Form form={profileForm} layout="vertical">
          <Form.Item label="Email">
            <Input value={user?.email} disabled />
          </Form.Item>
          <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
            <Input placeholder="First name" />
          </Form.Item>
          <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
            <Input placeholder="Last name" />
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            help={!profilePhoneValid ? 'Format: +1234567890 or +' : undefined}
            validateStatus={!profilePhoneValid ? 'error' : undefined}
          >
            <Input
              placeholder="+1234567890"
              onChange={(e) => {
                const v = e.target.value;
                setProfilePhoneValid(v === '' || /^\+\d*$/.test(v) || v === '+');
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

